<!-- One card, centred on a softly tinted page covered in a faint doodle
     wallpaper of school things. The tint, the glows and the doodles all come
     from the theme's brand scale, so the page follows whichever colour theme
     is chosen on this device. -->
<div class="login-backdrop relative min-h-screen bg-canvas overflow-hidden" *transloco="let t">
  <div class="login-glow login-glow-start" aria-hidden="true"></div>
  <div class="login-glow login-glow-end" aria-hidden="true"></div>
  <div class="login-doodles absolute inset-0 pointer-events-none" aria-hidden="true"></div>

  <main class="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-8 sm:py-12">
    <div class="w-full max-w-[28rem] bg-surface border border-borderLight rounded-card shadow-overlay p-6 sm:p-10">

      <!-- Wordmark, with the language switcher beside it: before the form, not
           after it, because somebody who reads only Kannada has to be able to
           switch before they can read the field labels. -->
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-2.5 min-w-0">
          <!-- The same mark as the sidebar, so the app looks like itself before
               and after signing in. Decorative: the name beside it is the label. -->
          <img src="logo.svg" alt="" class="w-9 h-9 object-contain shrink-0">

          <span class="text-base font-bold text-slateText tracking-tight truncate">School36TLabs</span>
        </div>
        <app-language-switcher class="shrink-0"></app-language-switcher>
      </div>

      <div class="mt-8">
        <h1 class="text-2xl font-bold text-slateText tracking-tight">{{ t('login.welcome') }}</h1>
        <p class="text-sm text-mutedText mt-1.5">{{ t('login.subtitle') }}</p>
      </div>

      @if (infoMessage && !errorMessage) {
        <div class="mt-6 flex gap-3 items-start rounded-control bg-indigo-50 border border-indigo-100 px-4 py-3"
             role="status" aria-live="polite">
          <lucide-icon [img]="Info" class="w-[18px] h-[18px] text-primary shrink-0 mt-px"></lucide-icon>
          <p class="text-sm text-primary-strong leading-relaxed">{{ infoMessage }}</p>
        </div>
      }

      @if (errorMessage) {
        <div class="mt-6 flex gap-3 items-start rounded-control bg-red-50 border border-red-200 px-4 py-3"
             role="alert" aria-live="polite">
          <lucide-icon [img]="AlertCircle" class="w-[18px] h-[18px] text-destructive shrink-0 mt-px"></lucide-icon>
          <p class="text-sm text-red-800 leading-relaxed">{{ errorMessage }}</p>
        </div>
      }

      <form (submit)="onSubmit($event)" class="mt-6 space-y-4" novalidate>

        <!-- Email -->
        <div>
          <label for="username" class="block text-sm font-medium text-slateText mb-1.5">
            {{ t('login.email') }}
          </label>
          <div class="relative">
            <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedText pointer-events-none flex"
                  aria-hidden="true">
              <lucide-icon [img]="Mail" class="w-[18px] h-[18px]"></lucide-icon>
            </span>
            <input
              type="text"
              id="username"
              name="username"
              autocomplete="username"
              [(ngModel)]="username"
              [attr.aria-invalid]="usernameInvalid ? 'true' : null"
              class="w-full pl-11 pr-4 py-2.5 text-sm bg-surface border rounded-control outline-none transition-shadow placeholder:text-faintText"
              [ngClass]="usernameInvalid
                ? 'border-destructive focus:ring-2 focus:ring-red-200'
                : 'border-borderLight hover:border-gray-300 focus:border-primary focus:ring-2 focus:ring-indigo-100'"
              [placeholder]="t('login.emailPlaceholder')"
            />
          </div>
        </div>

        <!-- Password -->
        <div>
          <label for="password" class="block text-sm font-medium text-slateText mb-1.5">{{ t('login.password') }}</label>
          <div class="relative">
            <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedText pointer-events-none flex"
                  aria-hidden="true">
              <lucide-icon [img]="Lock" class="w-[18px] h-[18px]"></lucide-icon>
            </span>
            <input
              [type]="showPassword ? 'text' : 'password'"
              id="password"
              name="password"
              autocomplete="current-password"
              [(ngModel)]="password"
              [attr.aria-invalid]="passwordInvalid ? 'true' : null"
              class="w-full pl-11 pr-11 py-2.5 text-sm bg-surface border rounded-control outline-none transition-shadow placeholder:text-faintText"
              [ngClass]="passwordInvalid
                ? 'border-destructive focus:ring-2 focus:ring-red-200'
                : 'border-borderLight hover:border-gray-300 focus:border-primary focus:ring-2 focus:ring-indigo-100'"
              placeholder="••••••••"
            />
            <button
              type="button"
              (click)="togglePassword()"
              [attr.aria-label]="showPassword ? t('login.hidePassword') : t('login.showPassword')"
              class="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-control text-mutedText hover:text-slateText hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition-colors"
            >
              <lucide-icon [img]="showPassword ? EyeOff : Eye" class="w-[18px] h-[18px]"></lucide-icon>
            </button>
          </div>
        </div>

        <!-- Remember and recovery share a row: both are things you do to the
             password field you have just left. -->
        <div class="flex items-center justify-between gap-4 pt-0.5">
          <label for="remember" class="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" id="remember" name="remember"
              class="w-4 h-4 rounded border-borderLight text-primary focus:ring-2 focus:ring-indigo-100 cursor-pointer" />
            <span class="text-sm text-slateText">{{ t('login.remember') }}</span>
          </label>
          <a href="#" class="text-sm font-medium text-primary hover:underline shrink-0">{{ t('login.forgot') }}</a>
        </div>

        <button
          type="submit"
          [disabled]="isLoading"
          class="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-control text-sm font-semibold text-white bg-primary hover:bg-indigo-700 active:bg-indigo-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed shadow-raised transition-colors !mt-6"
        >
          @if (isLoading) {
            <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ t('login.signingIn') }}
          } @else {
            {{ t('login.submit') }}
            <lucide-icon [img]="ArrowRight" class="w-4 h-4"></lucide-icon>
          }
        </button>
      </form>

      <!-- Demo accounts: development builds only (the list comes from the
           environment and is empty in a deployed build), and folded until asked
           for — a convenience next to the form, not part of it. -->
      @if (showDemo) {
        <div class="mt-6 pt-5 border-t border-borderLight">
          <button type="button" (click)="toggleDemo()"
            [attr.aria-expanded]="demoOpen" aria-controls="demo-accounts"
            class="w-full flex items-center justify-between gap-3 text-sm font-medium text-primary hover:text-primary-strong rounded-control focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-100 transition-colors">
            <span>{{ demoOpen ? t('login.hideDemo') : t('login.tryDemo') }}</span>
            <lucide-icon [img]="ChevronDown" class="w-4 h-4 transition-transform" [ngClass]="demoOpen ? 'rotate-180' : ''"></lucide-icon>
          </button>

          @if (demoOpen) {
            <div id="demo-accounts" class="mt-3">
              <!-- One per role. Two across on the narrowest screens (three would
                   clip "School Admin"), three once there is room. -->
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                @for (account of demoAccounts; track account.email) {
                  <button type="button" (click)="signInAsDemo(account.email)"
                    [disabled]="isLoading"
                    [title]="t('login.signInAs', { email: account.email })"
                    class="group px-2.5 py-2.5 rounded-control border border-borderLight bg-canvas hover:bg-indigo-50 hover:border-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition-colors text-left
                           disabled:opacity-60 disabled:cursor-not-allowed
                           flex flex-col">
                    <span class="text-xs font-semibold text-slateText group-hover:text-primary truncate">{{ account.label }}</span>
                    <span class="text-[11px] text-mutedText truncate mt-0.5">{{ account.scope }}</span>
                  </button>
                }
              </div>
              <p class="flex items-start gap-1.5 text-[11px] text-mutedText mt-3 leading-normal">
                <lucide-icon [img]="Info" class="w-3.5 h-3.5 text-primary shrink-0 mt-px"></lucide-icon>
                <span>{{ t('login.demoPasswordNote', { password: demoPassword }) }}</span>
              </p>
            </div>
          }
        </div>
      }
    </div>

    <!-- Help, then the mark. Support only links somewhere when there is an
         address to write to; otherwise the office that issued the login is
         the honest answer. -->
    <footer class="mt-6 text-center space-y-1">
      <p class="text-xs text-mutedText">
        {{ t('login.needHelp') }}
        @if (supportEmail) {
          <a [href]="'mailto:' + supportEmail" class="text-primary hover:text-primary-strong font-semibold hover:underline ml-1">{{ t('login.contactSupport') }}</a>
        } @else {
          {{ t('login.askOffice') }}
        }
      </p>
      <p class="text-[11px] text-faintText">{{ t('login.copyright', { year: year }) }}</p>
    </footer>
  </main>
</div>
